/**
 * Pure helper for when the Behavioural Observation Self-Report LLM paragraph
 * should be (re)generated on an Observation phase exit.
 *
 * Rules (layman's terms):
 *   - First draft: only when the caller explicitly allows it — today that is
 *     the forward step into Review & Synthesis (alongside Criteria / Other
 *     Behaviours), or Review's Finish catch-up if that exit never fired.
 *     Early phases (Task Admin, ADOS, CM back/side exits) never invent the
 *     paragraph for the first time.
 *   - Re-generation: once Self-Report already exists in stageSectionsGenerated,
 *     any later managed exit may refresh it when Self-Report inputs changed
 *     this visit (typed notes, re-tags, moves). That includes clearing the
 *     paragraph after the last note was removed.
 *
 * @param {Object} params
 * @param {boolean} params.hasContent - at least one note with non-empty text
 *   that feeds Self-Report (dedicated box or `self_reported` tag elsewhere)
 * @param {boolean} params.hasChanges - Self-Report inputs were edited this visit
 * @param {boolean} params.isGenerated - 'Self-Report' already in stageSectionsGenerated
 * @param {boolean} [params.allowInitialGeneration=false] - true only on the
 *   forward-to-Review (or Review catch-up) path that owns the first draft
 * @returns {boolean}
 */
export function shouldGenerateSelfReportSection({
  hasContent,
  hasChanges,
  isGenerated,
  allowInitialGeneration = false,
}) {
  // Already drafted → refresh only when this visit changed Self-Report inputs
  // (including emptying the section so the paragraph can clear).
  if (isGenerated) return Boolean(hasChanges);
  // First draft: reserved for the Review forward / catch-up path.
  return Boolean(allowInitialGeneration && hasContent);
}

/** Firestore key for the Self-Report general panel inside allTaskData. */
export const SELF_REPORT_DATA_KEY = 'general_self_reported';

/** Move-picker box id prefix for the Self-Report notes box. */
export const SELF_REPORT_BOX_ID_PREFIX = 'general:self_reported:';

/** Criteria-tag id that routes a note into the Self-Report paragraph (v2). */
export const SELF_REPORTED_TAG_ID = 'self_reported';

/**
 * True when a note carries the `self_reported` tag and has real text.
 * Used to treat cross-tagged Task Admin / ADOS notes as Self-Report content
 * for generation gating (the LLM already sweeps those tags).
 */
function noteIsSelfReportedContent(note) {
  return Boolean(
    note
    && String(note.text || '').trim()
    && Array.isArray(note.criteriaTags)
    && note.criteriaTags.includes(SELF_REPORTED_TAG_ID),
  );
}

/**
 * True when there is anything that should feed the Self-Report paragraph:
 *   1. At least one real note in the dedicated Self-Report box, OR
 *   2. At least one note elsewhere (task masterNotes, sensory notes, ADOS
 *      item notes) tagged `self_reported`.
 *
 * Whitespace-only notes do not count — they would produce an empty paragraph.
 *
 * @param {Object} allTaskData - Per-task/panel data keyed by task id or panel key
 * @param {Object} [adosData] - ADOS scoring map (`ados_<id>_notes` arrays)
 * @returns {boolean}
 */
export function hasSelfReportNoteContent(allTaskData, adosData = {}) {
  const panelNotes = allTaskData?.[SELF_REPORT_DATA_KEY]?.notes;
  if (Array.isArray(panelNotes) && panelNotes.some((n) => n && String(n.text || '').trim())) {
    return true;
  }

  // Sweep other Task Admin / general panels for explicitly tagged notes.
  // The Self-Report panel itself is already covered above (even without a tag,
  // notes typed there are self-report by definition).
  for (const [key, entry] of Object.entries(allTaskData || {})) {
    if (key === SELF_REPORT_DATA_KEY) continue;
    if (!entry || typeof entry !== 'object') continue;
    for (const field of ['masterNotes', 'notes']) {
      const arr = entry[field];
      if (!Array.isArray(arr)) continue;
      if (arr.some(noteIsSelfReportedContent)) return true;
    }
  }

  // ADOS item note arrays (ados_<itemId>_notes).
  for (const value of Object.values(adosData || {})) {
    if (!Array.isArray(value)) continue;
    if (value.some(noteIsSelfReportedContent)) return true;
  }

  return false;
}

/**
 * True when a cross-box note move involves the Self-Report box in either
 * direction. Clinicians can move notes both in and out of Self-Report from any
 * OBS phase, and both directions change what the paragraph should say, so both
 * must mark the section dirty.
 *
 * @param {string} sourceId - Box id the note came from
 * @param {string} targetId - Box id the note was dropped into
 * @returns {boolean}
 */
export function noteMoveTouchesSelfReport(sourceId, targetId) {
  return String(sourceId).startsWith(SELF_REPORT_BOX_ID_PREFIX)
    || String(targetId).startsWith(SELF_REPORT_BOX_ID_PREFIX);
}

/**
 * Build a stable signature of the notes in an array that carry the
 * `self_reported` tag, capturing both which notes are tagged and their text.
 *
 * This is how Task Administration (and ADOS coding) detect that a clinician has
 * cross-tagged an ordinary task/ADOS note into the Self-Report paragraph — a
 * v2-only affordance. Because the Self-Report LLM input sweeps every
 * `self_reported`-tagged note (not just the dedicated panel), any change to
 * this signature between the old and new value of a note array means the
 * Self-Report paragraph could read differently and should be marked dirty.
 *
 * Comparing signatures (rather than dirtying on every keystroke) keeps us from
 * firing a Self-Report regeneration when the clinician only edited notes that
 * were never tagged for Self-Report.
 *
 * @param {Array} notes - a note array (e.g. a task's masterNotes)
 * @returns {string} order-independent signature of `id:text` for tagged notes
 */
export function selfReportedTagSignature(notes) {
  if (!Array.isArray(notes)) return '';
  return notes
    .filter((n) => n
      && Array.isArray(n.criteriaTags)
      && n.criteriaTags.includes(SELF_REPORTED_TAG_ID))
    .map((n) => `${n.id}:${String(n.text || '').trim()}`)
    .sort()
    .join('|');
}

/**
 * True when the set (or text) of `self_reported`-tagged notes differs between
 * two versions of a note array — i.e. an edit that changes what the Self-Report
 * paragraph would say.
 *
 * @param {Array} prevNotes - note array before the edit
 * @param {Array} nextNotes - note array after the edit
 * @returns {boolean}
 */
export function selfReportedTaggingChanged(prevNotes, nextNotes) {
  return selfReportedTagSignature(prevNotes) !== selfReportedTagSignature(nextNotes);
}

/**
 * True when a note can feed a Criteria / Other Behaviours paragraph — i.e. it
 * has real text and at least one tag that is NOT the Self-Report tag.
 *
 * Self-Report panel notes are auto-tagged `self_reported`. That alone must not
 * dirty Criteria regeneration. A note that is also tagged A1–B4 or `other`
 * does feed Criteria, so text/tag edits there should dirty both sections.
 */
export function noteFeedsCriteriaParagraph(note) {
  if (!note || !String(note.text || '').trim()) return false;
  if (!Array.isArray(note.criteriaTags)) return false;
  return note.criteriaTags.some((t) => t && t !== SELF_REPORTED_TAG_ID);
}

/**
 * Stable signature of notes that feed Criteria / Other Behaviours (id + text +
 * non-Self-Report tags). Used to dirty Criteria only when those inputs change.
 *
 * @param {Array} notes
 * @returns {string}
 */
export function criteriaRelevantNoteSignature(notes) {
  if (!Array.isArray(notes)) return '';
  return notes
    .filter(noteFeedsCriteriaParagraph)
    .map((n) => {
      const tags = (n.criteriaTags || [])
        .filter((t) => t && t !== SELF_REPORTED_TAG_ID)
        .slice()
        .sort()
        .join(',');
      return `${n.id}:${String(n.text || '').trim()}:${tags}`;
    })
    .sort()
    .join('|');
}

/**
 * True when the Criteria-relevant content of a note array changed between two
 * versions (text or DSM/`other` tags). Self-Report-only edits return false.
 *
 * @param {Array} prevNotes
 * @param {Array} nextNotes
 * @returns {boolean}
 */
export function criteriaRelevantNotesChanged(prevNotes, nextNotes) {
  return criteriaRelevantNoteSignature(prevNotes) !== criteriaRelevantNoteSignature(nextNotes);
}
